import React from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { Home, FolderKanban, Users, FileText, MoreHorizontal, Sparkles, LogOut, Package, Wallet, Settings, Menu, Scissors, BellRing, MessageSquareText, ClipboardList, Boxes, BarChart3, CreditCard } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAssistant } from '@/context/AssistantContext';
import { AIAssistant } from '@/components/AIAssistant';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

const PRIMARY_NAV = [
  { to: '/beranda', label: 'Beranda', icon: Home, testid: 'bottom-nav-beranda', end: true },
  { to: '/proyek', label: 'Proyek', icon: FolderKanban, testid: 'bottom-nav-proyek' },
  { to: '/penawaran', label: 'Penawaran', icon: FileText, testid: 'bottom-nav-penawaran' },
  { to: '/pelanggan', label: 'Pelanggan', icon: Users, testid: 'bottom-nav-pelanggan' },
];

const MORE_NAV = [
  { to: '/wa-inbox', label: 'Inbox WhatsApp (AI)', icon: MessageSquareText },
  { to: '/followup', label: 'Follow-up (AI)', icon: BellRing },
  { to: '/optimasi', label: 'Optimasi Potong (AI)', icon: Scissors },
  { to: '/inventory', label: 'Inventory / Stok', icon: Boxes },
  { to: '/laporan', label: 'Laporan Profit', icon: BarChart3 },
  { to: '/katalog', label: 'Katalog & Harga', icon: Package },
  { to: '/pembayaran', label: 'Pembayaran', icon: Wallet },
  { to: '/langganan', label: 'Langganan', icon: CreditCard },
  { to: '/pengaturan', label: 'Pengaturan Toko', icon: Settings },
];

const ALL_DESKTOP_NAV = [
  ...PRIMARY_NAV,
  { to: '/wa-inbox', label: 'Inbox WhatsApp', icon: MessageSquareText },
  { to: '/followup', label: 'Follow-up', icon: BellRing },
  { to: '/optimasi', label: 'Optimasi Potong', icon: Scissors },
  { to: '/inventory', label: 'Inventory / Stok', icon: Boxes },
  { to: '/laporan', label: 'Laporan Profit', icon: BarChart3 },
  { to: '/katalog', label: 'Katalog & Harga', icon: Package },
  { to: '/pembayaran', label: 'Pembayaran', icon: Wallet },
  { to: '/langganan', label: 'Langganan', icon: CreditCard },
  { to: '/pengaturan', label: 'Pengaturan', icon: Settings },
];

const FIELD_PRIMARY = [{ to: '/tugas', label: 'Tugas Saya', icon: ClipboardList, testid: 'bottom-nav-tugas', end: true }];

const PLAN_LABEL = { free: 'Gratis', pro: 'Pro', business: 'Bisnis' };
const PLAN_PILL = {
  free: 'border-slate-300 bg-slate-100 text-slate-600',
  pro: 'border-primary/30 bg-primary/10 text-primary',
  business: 'border-amber-300 bg-amber-50 text-amber-700',
};

function PlanBadge() {
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => { let m = true; api.get('/billing/me').then((r) => m && setInfo(r.data)).catch(() => {}); return () => { m = false; }; }, []);
  if (!info) return null;
  const trialDays = info.status === 'trial' && info.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(info.trial_ends_at).getTime() - Date.now()) / 86400000)) : null;
  return (
    <Link to="/langganan" data-testid="sidebar-plan-badge"
      className="mb-2 flex items-center justify-between gap-2 rounded-xl border bg-background px-3 py-2 transition-colors hover:bg-muted">
      <div className="flex items-center gap-2 text-xs">
        <CreditCard className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Paket {PLAN_LABEL[info.plan] || info.plan}</span>
      </div>
      {trialDays != null
        ? <Badge variant="outline" className="rounded-full border-sky-200 bg-sky-50 text-[10px] text-sky-700">{trialDays}h trial</Badge>
        : <Badge variant="outline" className={cn('rounded-full text-[10px]', PLAN_PILL[info.plan])}>{info.status === 'expired' ? 'Berakhir' : 'Aktif'}</Badge>}
    </Link>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { setOpen } = useAssistant();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const initials = (user?.nama || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const isField = user?.role === 'field';
  const primaryNav = isField ? FIELD_PRIMARY : PRIMARY_NAV;
  const desktopNav = isField ? FIELD_PRIMARY : ALL_DESKTOP_NAV;
  const moreNav = isField ? [] : MORE_NAV;
  const showAssistant = !isField;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-card md:flex">
        <div className="flex items-center gap-2 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground font-display font-bold">A</div>
          <div>
            <div className="font-display text-base font-semibold leading-tight">AluminaOne</div>
            <div className="text-xs text-muted-foreground">{isField ? 'Aplikasi Tukang' : 'Operasi + Asisten AI'}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {desktopNav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => cn('flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}>
              <item.icon className="h-5 w-5" /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-3">
          {showAssistant && (
            <button onClick={() => setOpen(true)} data-testid="ai-assistant-open-button-sidebar"
              className="mb-2 flex w-full items-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
              <Sparkles className="h-4 w-4" /> Buka Asisten AI
            </button>
          )}
          {!isField && <PlanBadge />}
          <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-2 py-2">
            <Avatar className="h-8 w-8"><AvatarFallback className="bg-secondary text-xs">{initials}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.nama}</div>
              <div className="text-xs capitalize text-muted-foreground">{user?.role}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="sidebar-logout-button"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-semibold text-foreground transition-colors duration-150 hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </aside>

      {/* Mobile top header */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b bg-card/95 px-4 py-3 backdrop-blur md:hidden">
        <div className="flex items-center gap-2">
          <button onClick={() => setMoreOpen(true)} data-testid="mobile-menu-button" aria-label="Menu"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground hover:bg-muted">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-display text-sm font-bold">A</div>
          <span className="font-display text-base font-semibold">AluminaOne</span>
        </div>
        {showAssistant ? (
          <button onClick={() => setOpen(true)} data-testid="ai-assistant-open-button"
            className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
            <Sparkles className="h-4 w-4" /> Asisten
          </button>
        ) : (
          <span className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold capitalize text-secondary-foreground">{user?.role}</span>
        )}
      </header>

      {/* Main content */}
      <main className="px-4 pb-24 pt-4 md:ml-64 md:px-8 md:pb-10 md:pt-6">
        <div className="mx-auto max-w-6xl">{children}</div>
      </main>

      {/* Floating assistant button (desktop) */}
      {showAssistant && (
        <button onClick={() => setOpen(true)} aria-label="Buka Asisten AI"
          className="fixed bottom-6 right-6 z-30 hidden h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 md:flex">
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-stretch border-t bg-card/95 backdrop-blur md:hidden">
        {primaryNav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
            className={({ isActive }) => cn('flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground')}>
            {({ isActive }) => (
              <>
                <span className={cn('flex h-7 w-7 items-center justify-center rounded-full', isActive && 'bg-primary/10')}>
                  <item.icon className="h-5 w-5" />
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(true)} data-testid="bottom-nav-lainnya" className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium text-muted-foreground">
          <span className="flex h-7 w-7 items-center justify-center rounded-full"><MoreHorizontal className="h-5 w-5" /></span>
          Lainnya
        </button>
      </nav>

      {/* Shared "Lainnya" sheet (triggered from header menu + bottom nav) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="text-left"><SheetTitle className="font-display">Menu Lainnya</SheetTitle></SheetHeader>
          <div className="mt-3 grid grid-cols-1 gap-1">
            {moreNav.map((item) => (
              <button key={item.to} onClick={() => { setMoreOpen(false); navigate(item.to); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium hover:bg-muted">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><item.icon className="h-5 w-5" /></span>
                {item.label}
              </button>
            ))}
            <button onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-destructive hover:bg-muted">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10 text-destructive"><LogOut className="h-5 w-5" /></span>
              Keluar ({user?.nama})
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {showAssistant && <AIAssistant />}
    </div>
  );
}
